import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Spinner, Button, Row, Col, Modal } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Dropdown as AntDropdown,
  Button as AntButton,
  Input,
  Select,
  Empty,
  Table,
} from "antd";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import SubtleBadge from "components/common/SubtleBadge";
import useUsers from "hooks/userManagement/useUsers";
import IconButton from "components/common/IconButton";
import Guard from "components/access-control/rbac/Guard";
import useDebounce from "hooks/route-rules/useDebounce";
import useFormattedDate from "hooks/useFormatDate";
import CopyableText from "components/common/CopyableText";
import { EllipsisOutlined } from "@ant-design/icons";

const STATUS_COLORS = {
  ACTIVE: "success",
  PENDING: "warning",
  DISABLED: "secondary",
  SUSPENDED: "warning",
  DELETED: "danger",
  UNKNOWN: "secondary",
};

// Real colors for stats section (Bootstrap matching tones)
const STATUS_STYLES = {
  ACTIVE: { border: "#198754", text: "#198754", bg: "#e6f4ea" },      // green
  PENDING: { border: "#ffc107", text: "#b08900", bg: "#fff8e1" },     // yellow
  DISABLED: { border: "#6c757d", text: "#6c757d", bg: "#f1f3f5" },    // gray
  SUSPENDED: { border: "#fd7e14", text: "#fd7e14", bg: "#fff4e6" },   // orange
  DELETED: { border: "#dc3545", text: "#dc3545", bg: "#fdeaea" },     // red
  UNKNOWN: { border: "#6c757d", text: "#6c757d", bg: "#f1f3f5" },
};

const STATUS_FILTER_OPTIONS = [
  { label: "🌍 All Statuses", value: "ALL" },
  { label: "🟢 Active", value: "ACTIVE" },
  { label: "🟡 Pending", value: "PENDING" },
  { label: "⚪ Disabled", value: "DISABLED" },
  { label: "🟠 Suspended", value: "SUSPENDED" },
  { label: "🔴 Deleted", value: "DELETED" },
  { label: "❔ Unknown", value: "UNKNOWN" },
];


// Compute stats including all statuses
const computeStats = (users) => {
  const allStatuses = STATUS_FILTER_OPTIONS.map((s) => s.value).filter((v) => v !== "ALL");
  const counts = allStatuses.reduce((acc, status) => ({ ...acc, [status]: 0 }), {});
  if (!users) return counts;
  users.forEach((u) => {
    const status = u.status || "UNKNOWN";
    counts[status] = (counts[status] || 0) + 1;
  });
  return counts;
};

const Users = ({ staff = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const usersListPath = `${location.pathname}${location.search}`;
  const { formatDateTime } = useFormattedDate();

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Delete state
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Search
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const isSearchActive = debouncedSearchTerm.trim().length >= 3;

  const [firstLoad, setFirstLoad] = useState(true);

  const { users, allUsersForStats, loading, error, totalPages, totalElements, refetch, searchUserProfiles, getUserById, deleteUserProfileProperty } =
    useUsers(page, pageSize, staff);

  // If a user was just created, force a refresh for that specific user id so they
  // land in the correct table (staff vs customers) without needing to open details.
  useEffect(() => {
    if (typeof window === "undefined") return;

    let payload = null;
    try {
      payload = JSON.parse(
        window.sessionStorage.getItem("olitaskLastCreatedUser") || "null",
      );
    } catch {
      payload = null;
    }

    const createdUserId = payload?.id;
    const createdStaff =
      typeof payload?.staff === "boolean" ? payload.staff : null;

    if (!createdUserId) return;
    if (createdStaff !== null && createdStaff !== staff) return;

    // Clear early to avoid loops if the refresh triggers rerenders.
    window.sessionStorage.removeItem("olitaskLastCreatedUser");

    (async () => {
      try {
        await getUserById(createdUserId);
      } catch {
        // ignore: refetch still helps in many cases
      }
      try {
        await refetch();
      } catch {
        // ignore
      }
    })();
  }, [getUserById, refetch, staff]);

  // Reset page on search or status change
  useEffect(() => setPage(0), [debouncedSearchTerm, selectedStatus, staff]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isSearchActive) {
          await searchUserProfiles(debouncedSearchTerm, selectedStatus, page);
        } else {
          await refetch();
        }
      } finally {
        setFirstLoad(false);
      }
    };
    fetchData();
  }, [debouncedSearchTerm, page, isSearchActive, searchUserProfiles, selectedStatus, refetch]);

  // Refetch on window focus
  useEffect(() => {
    const handleFocus = () => refetch();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refetch]);

  // Keep the latest table URL so user details can always navigate back correctly.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem("olitaskUsersListPath", usersListPath);
  }, [usersListPath]);

  // Action handlers
  const handleViewUser = useCallback(
    (id) =>
      navigate(`/olitask/users/${id}`, {
        state: { fromPath: usersListPath },
      }),
    [navigate, usersListPath],
  );
  const handleEditUser = useCallback(
    (id) =>
      navigate(`/olitask/users/${id}/edit`, {
        state: { fromPath: usersListPath },
      }),
    [navigate, usersListPath],
  );
  const confirmDelete = (id) => { setUserToDelete(id); setShowDeleteModal(true); setDeleteError(null); };

  const handleDelete = async () => {
    if (!userToDelete) return;
    setDeleting(true); setDeleteError(null);
    try {
      await deleteUserProfileProperty(userToDelete);
      toast.success("User profile deleted successfully.");
      await refetch();
    } catch (err) {
      const apiMessage = err?.response?.data?.message || err?.message || "Failed to delete user profile.";
      setDeleteError(apiMessage);
      toast.error(apiMessage);
    } finally { setShowDeleteModal(false); setUserToDelete(null); setDeleting(false); }
  };

  const handleCancelDelete = () => { setShowDeleteModal(false); setUserToDelete(null); setDeleteError(null); };


  const stats = useMemo(() => computeStats(allUsersForStats), [allUsersForStats]);

  // Table columns
  const columns = useMemo(
    () => [
      {
        title: "User ID",
        dataIndex: "id",
        key: "id",
        width: 330,
        render: (value, record) => (
          <div style={{ whiteSpace: "nowrap", fontSize: 12 }}>
            <span
              className="user-id-cell"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                if (e?.target?.closest?.(".ant-typography-copy")) return;
                handleViewUser(record.id);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleViewUser(record.id);
              }}
              style={{ cursor: "pointer" }}
            >
              <CopyableText
                value={value}
                showTooltip={false}
                stopPropagation={false}
              />
            </span>
          </div>
        ),
      },
      {
        title: "Name",
        key: "fullName",
        width: 170,
        render: (_, row) => {
          const fullName = `${row.firstName || ""} ${row.lastName || ""}`.trim();
          return (
            <div
              title={fullName}
              style={{
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fullName || "None"}
            </div>
          );
        },
        sorter: (a, b) =>
          `${a.firstName || ""} ${a.lastName || ""}`
            .trim()
            .localeCompare(`${b.firstName || ""} ${b.lastName || ""}`.trim()),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: 240,
        render: (v) => v || "None",
        ellipsis: true,
        sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
      },
      {
        title: "Gender",
        dataIndex: "gender",
        key: "gender",
        width: 110,
        render: (v) => v || "None",
        sorter: (a, b) => (a.gender || "").localeCompare(b.gender || ""),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        align: "center",
        width: 130,
        render: (value) => {
          const status = value || "UNKNOWN";
          const colorTheme = STATUS_COLORS[status] || "secondary";
          return (
            <SubtleBadge
              bg={colorTheme}
              className="fw-bold"
              style={{
                borderRadius: "4px",
                padding: "2px 6px",
                fontSize: "0.7rem",
                textTransform: "uppercase",
              }}
            >
              {status.replaceAll("_", " ")}
            </SubtleBadge>
          );
        },
        sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
      },
      {
        title: "Created At",
        dataIndex: "createdAt",
        key: "createdAt",
        align: "center",
        width: 200,
        render: (v) => formatDateTime(v),
        sorter: (a, b) =>
          new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime(),
      },
      {
        title: "Actions",
        key: "actions",
        width: 90,
        render: (_, userRow) => {
          const items = [
            { key: "view", label: "View Details" },
            {
              key: "edit",
              label: (
                <Guard privilege="UPDATE_USERS">
                  <span>Edit</span>
                </Guard>
              ),
            },
            { type: "divider" },
            {
              key: "delete",
              label: (
                <Guard privilege="DELETE_USERS">
                  <span className="text-danger">Delete</span>
                </Guard>
              ),
              danger: true,
            },
          ];

          return (
            <span
              className="action-dropdown"
              onClick={(e) => e.stopPropagation()}
              role="presentation"
            >
              <AntDropdown
                trigger={["click"]}
                placement="bottomRight"
                menu={{
                  items,
                  onClick: ({ key, domEvent }) => {
                    domEvent?.stopPropagation?.();
                    if (key === "view") handleViewUser(userRow.id);
                    if (key === "edit") handleEditUser(userRow.id);
                    if (key === "delete") confirmDelete(userRow.id);
                  },
                }}
              >
                <AntButton
                  type="text"
                  size="small"
                  icon={<EllipsisOutlined />}
                  onClick={(e) => e.stopPropagation()}
                />
              </AntDropdown>
            </span>
          );
        },
      },
    ],
    [formatDateTime, handleEditUser, handleViewUser],
  );

  const tableData = useMemo(() => {
    if (!users) return [];
    if (selectedStatus === "ALL") return users;
    return users.filter((u) => (u?.status || "UNKNOWN") === selectedStatus);
  }, [users, selectedStatus]);

  return (
    <>
      <WidgetSectionTitle
        className="mb-3 mt-4"
        title={staff ? "Staff Profiles" : "Customer Profiles"}
        icon="users"
        subtitle={`Manage ${staff ? "staff" : "customer"} user profiles.`}
        transform="shrink-2"
      />

    {/* ================= STATS + ADD BUTTON ================= */}
<div
  className="position-relative p-3 rounded-3 mb-4 d-flex align-items-center justify-content-between flex-wrap"
  style={{
    background: "#f7f5f5",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  }}
>
  {/* Stats boxes */}
  <div className="d-flex flex-nowrap gap-2 overflow-auto">
    {Object.keys(stats).map((status) => {
      const style = STATUS_STYLES[status] || STATUS_STYLES.UNKNOWN;

      return (
        <div
          key={status}
          className="p-2 rounded-3 d-flex flex-column align-items-center justify-content-center"
          role="button"
          tabIndex={0}
          onClick={() => {
            setSelectedStatus(status);
            setPage(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setSelectedStatus(status);
              setPage(0);
            }
          }}
          style={{
            minWidth: 70,           // smaller min width
            maxWidth: 90,           // max width stays constrained
            flex: "0 1 auto",       // allows shrinking if too many boxes
            backgroundColor: "#fff",
            borderLeft: `4px solid ${style.border}`,
            cursor: "pointer",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: style.text,
              marginBottom: 4,
              textAlign: "center",
            }}
          >
            {status}
          </div>

          <div
            style={{
              fontSize: 16,          // slightly smaller number
              fontWeight: 700,
              color: style.text,
            }}
          >
            {stats[status]}
          </div>
        </div>
      );
    })}
  </div>

  {/* Add button on right side with color */}
  <div className="ms-auto mt-2 mt-sm-0">
    <IconButton
      size="sm"
      icon="plus"
      transform="shrink-3"
      onClick={() => navigate(`/olitask/users/add?staff=${staff}`)}
      style={{
        backgroundColor: "#198754",
        color: "#fff",
        border: "1px solid #198754",
        fontWeight: 600,
      }}
    >
      <span className="ms-1">Add {staff ? "Staff" : "Customer"}</span>
    </IconButton>
  </div>
</div>

      {/* ================= SEARCH & FILTER ================= */}
{/* ================= SEARCH + STATUS FILTER ================= */}
<Row className="mb-3 g-3 align-items-center">
  <Col md={8} lg={9}>
    <div className="d-flex align-items-center gap-3 flex-wrap">
      <Input
        prefix={<span style={{ fontSize: 14 }}>🔍</span>}
        placeholder="Search by name, email or ID..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        allowClear
        size="large"
        style={{
          borderRadius: 12,
          height: 30,
          flex: "1 1 300px",
          maxWidth: 300
        }}
      />

      <Select
        size="large"
        value={selectedStatus}
        onChange={(value) => {
          setSelectedStatus(value);
          setPage(0);
        }}
        options={STATUS_FILTER_OPTIONS}
        style={{
          minWidth: 180,
          height: 28
        }}
      />
    </div>
  </Col>

  <Col md={4} lg={3} className="text-end">
    <div style={{ fontSize: 13, color: "#64748b" }}>
      Results:{" "}
      <span className="fw-semibold text-dark">
        {tableData.length}
      </span>{" "}
      of{" "}
      <span className="fw-semibold text-dark">
        {totalElements}
      </span>
    </div>
  </Col>
</Row>

      {/* ================= RESULTS & TABLE ================= */}
      {/* <div className="d-flex justify-content-end mb-0.5" style={{ fontSize: 13, color: "#64748b" }}>
        Results: <span className="fw-semibold text-dark">{tableData.length}</span> of <span className="fw-semibold text-dark">{totalElements}</span>
      </div> */}

      {firstLoad || loading ? (
        <div style={{ minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Spinner animation="border" variant="success" />
        </div>
      ) : tableData.length === 0 ? (
        <div style={{ minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Empty description={`No ${staff ? "staff" : "customer"} profiles found`} />
        </div>
      ) : (
        <div style={{ width: "100%", overflowX: "hidden" }}>
          <Table
            rowKey="id"
            dataSource={tableData}
            columns={columns}
            size="small"
            bordered
            className="custom-table"
            tableLayout="fixed"
            scroll={{ x: 1200 }}
            pagination={{
              current: page + 1,
              pageSize,
              total: totalElements,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 30, 50, 100, 200, 500],
              onChange: (p, size) => {
                setPage(p - 1);
                setPageSize(size);
              },
            }}
            onRow={(record) => ({
              onClick: (e) => {
                // Prevent row navigation when interacting with actions/copy controls.
                if (e?.target?.closest?.(".action-dropdown")) return;
                if (e?.target?.closest?.(".ant-typography-copy")) return;
                handleViewUser(record.id);
              },
            })}
          />
        </div>
      )}

      {error && <div className="alert alert-danger mt-2">{error}</div>}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this user profile? This action cannot be undone.</p>
          {deleteError && <div className="alert alert-warning mt-3">{deleteError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Deleting...</> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Users;
